/* eslint-disable camelcase */
import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gql, useReactiveVar } from '@apollo/client';

import { LocaleContext } from 'app/containers/LocaleProvider';

import {
    discountConditionsInitialType,
    discountConditionsType,
} from 'types';

import { UIButton, UIButtonBottomArea } from 'app/components/ui';
import FloatFooterDiscountCounter from 'app/components/FloatFooterDiscountCounter';
import SkeletonItem from 'app/components/SkeletonItem';
import formatPrice from 'app/utils/price';

import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { isGlobalLoadingVar } from 'app/apollo/reaction';
import PlusIcon from './assets/plus.component.svg';

// import { selectedPeriodVar } from 'app/apollo/reaction';

import './open-basket-button.scss';
import { useBasketDataByLocation } from './hooks';
import { CookiesNotification } from '../ui/CookiesNotification/CookiesNotification';

const locales = {
    ru: {
        cart: 'Корзина',
    },
    en: {
        cart: 'Cart',
    },
};


function CenterContent({
    title = 'Корзина',
    price,
    originalPrice = 0,
    rowPriceOnly,
}) {
    const { locale } = useContext(LocaleContext);

    const text = title ?? locales[locale].cart;

    const hasDiscount = Boolean(originalPrice && originalPrice !== price);

    if (rowPriceOnly) {
        return (
            <>
                <span className="title">
                    {text}
                </span>
                <span className="price" styleName="open-basket-button-price">
                    {locale === 'en' && (<span className="rub">₽</span>)}
                    <span className="num">{formatPrice(originalPrice)}</span>
                    {locale === 'ru' && (<span className="rub">₽</span>)}
                </span>
            </>
        );
    }

    return (
        <>
            <span className="title">{text}</span>
            {price > 0 && (
                <span className="price" styleName="open-basket-button-price">
                    {locale === 'en' && (<span className="rub">₽</span>)}
                    <span className="num">{formatPrice(price)}</span>
                    {locale === 'ru' && (<span className="rub">₽</span>)}
                </span>
            )}
            {hasDiscount && (
                <span className="original-price">
                    <span className="num">{formatPrice(originalPrice)}</span>
                    {/* <span className="num">3 000</span> */}
                    <span className="rub">₽</span>
                </span>
            )}

        </>
    );
}
CenterContent.propTypes = {
    title: PropTypes.string,
    price: PropTypes.number.isRequired,
    originalPrice: PropTypes.number,
    rowPriceOnly: PropTypes.bool,
};

export default function OpenBasketButton({
    isNeededRenderSkeleton = false,
    onClick = () => { },
    loading = false,
    promocodeConditions = discountConditionsInitialType,
    position = 'fixed',
    elType = 'button',
    href = '',
    title = 'Корзина',
    showPlusIcon = false,
    rowPriceOnly = false,
}) {
    const isGlobalLoading = useReactiveVar(isGlobalLoadingVar);
    const { cart } = useBasketDataByLocation();
    const { segment } = useContext(PathSegmentContext);

    /*
        differenceBetweenPromocodeAndOrder, discountValue, discountType,
    */

    const price = useMemo(
        () => cart?.totals?.total_discount_price || 0,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cart?.totals?.total_discount_price, isGlobalLoading],
    );

    const originalPrice = useMemo(
        // eslint-disable-next-line no-unsafe-optional-chaining
        () => {
            if (segment === 'delivery') return 0;
            const t_price = cart?.totals?.total_price || 0;
            const d_price = cart?.totals.delivery_price || 0;
            return t_price + d_price;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cart?.totals?.total_price, cart?.totals?.delivery_price, isGlobalLoading],
    );

    /**
      * @TODO Отрефакторить логику выбора цена. Попробовать вынести в один слой.
      * Тaк как во многих компонентах дублируется логика. Например в Basket.js 595 строка.
     */
    // const canRenderOpenBasketButton = price > 0;


    // const basketPreviewTestValue = abTestDataContext?.basket_preview_in_menu === 'with_preview';


    if (isNeededRenderSkeleton) {
        return (
            <div aria-label="main-dishlisk-button">
                <UIButtonBottomArea zIndex="6">
                    <div styleName="basket-button-skeleton">
                        <SkeletonItem />
                    </div>
                </UIButtonBottomArea>
            </div>
        );
    }

    return (
        <div aria-label="main-dishlisk-button">
            <UIButtonBottomArea zIndex="6" position={position}>
                <FloatFooterDiscountCounter promocodeConditions={cart?.discount_conditions || promocodeConditions} />
                <CookiesNotification pageClass="promoCheckout" />
                <UIButton
                    centerContent={(
                        <div styleName="center-content-wrapper">
                            <CenterContent
                                price={price}
                                originalPrice={originalPrice}
                                title={title}
                                rowPriceOnly={rowPriceOnly}
                            />
                            {showPlusIcon && <PlusIcon style={{ paddingLeft: 4 }} />}
                        </div>
                    )}
                    onClick={onClick}
                    // onClick={() => {
                    //     handler();
                    // }}
                    disabled={isGlobalLoading}
                    loading={loading || isGlobalLoading}
                    aria-label="Корзина"
                    data-test-id="open-basket-button"
                    elType={elType}
                    href={href}
                />
            </UIButtonBottomArea>
        </div>
    );
}


OpenBasketButton.propTypes = {
    isNeededRenderSkeleton: PropTypes.bool,
    price: PropTypes.number,
    originalPrice: PropTypes.number,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    promocodeConditions: PropTypes.shape(discountConditionsType),
    position: PropTypes.string,
    elType: PropTypes.string,
    href: PropTypes.string,
    title: PropTypes.string,
    showPlusIcon: PropTypes.bool,
};
